import { useState, useEffect } from "react";
import axios from "axios";
import "./Support.css";
import webserviceUrl, { BASE_URL } from "../../API/webserviceUrl";

// ============================================================
// API
// ============================================================

const api = axios.create({
  baseURL: BASE_URL,
});

// Attach token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

// ============================================================
// CONSTANTS
// ============================================================

const ROLES = [
  { id: "participant", label: "Participant" },
  { id: "lead", label: "LEAD" },
  { id: "admin", label: "ADMIN" },
];

const IMAGE_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "svg",
  "jfif",
];

// ============================================================
// HELPERS
// ============================================================

function getFullUrl(url) {
  if (!url) return "";

  // Already a complete URL
  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  // Relative URL
  return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
}

function getFileExt(url) {
  if (!url) return "";

  const clean = url.split("?")[0];
  const parts = clean.split(".");

  return parts.length > 1
    ? parts[parts.length - 1].toLowerCase()
    : "";
}

function getFileName(url) {
  if (!url) return "";

  const clean = url.split("?")[0];

  return clean.substring(clean.lastIndexOf("/") + 1);
}

function isImageUrl(url) {
  return IMAGE_EXTENSIONS.includes(getFileExt(url));
}

// ============================================================
// MAP API POST -> UI THREAD
// ============================================================

function mapPostToThread(post) {
  let parsedComments = [];

  if (post.comments) {
    try {
      const raw =
        typeof post.comments === "string"
          ? JSON.parse(post.comments)
          : post.comments;

      parsedComments = (raw || []).map((c) => ({
        id: c.comment_id,
        author: c.commented_sender_name,
        role:
          c.commented_sender_name === "LEAD"
            ? "lead"
            : "participant",
        text: c.comment,
        at: c.commented_date,
        likes: Number(c.comment_likes || 0),
        selfLiked: !!Number(c.self_liked || 0),
      }));
    } catch (err) {
      console.error(
        "Failed to parse comments for post",
        post.post_id,
        err
      );
    }
  }

  return {
    id: post.post_id,

    title: post.post_description,

    description: post.post_description,

    raisedBy: post.created_sender_name,

    // IMPORTANT:
    // Take status directly from API
    status: post.status || "open",

    createdAt: post.post_date || post.created_date,

    image: post.image_url || null,

    likes: Number(post.likes || 0),

    selfLiked: !!Number(post.self_liked || 0),

    commentCount: Number(post.comment_count || 0),

    messages: [
      {
        author: post.created_sender_name,
        role: "participant",
        text: post.post_description,
        at: post.post_date || post.created_date,
      },

      ...parsedComments,
    ],
  };
}

// ============================================================
// DATE FORMAT
// ============================================================

function toSqlDateTime(date) {
  const pad = (n) => String(n).padStart(2, "0");

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate()
    )} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds()
    )}`
  );
}

// ============================================================
// STATUS HELPERS
// ============================================================

function getStatusLabel(status) {
  switch (status) {
    case "open":
      return "Open";

    // case "in_progress":
    //   return "In Progress";

    case "resolved":
      return "Resolved";

    default:
      return status || "Open";
  }
}

function getStatusClass(status) {
  switch (status) {
    case "open":
      return "sup-status-open";

    // case "in_progress":
    //   return "sup-status-in-progress";

    case "resolved":
      return "sup-status-resolved";

    default:
      return "sup-status-open";
  }
}

// ============================================================
// COMPONENT
// ============================================================

export default function Support() {
  const [currentRole, setCurrentRole] = useState("participant");

  const [threads, setThreads] = useState([]);

  const [loading, setLoading] = useState(true);

  const [errorMsg, setErrorMsg] = useState("");

  const [activeId, setActiveId] = useState(null);

  const [showNewIssue, setShowNewIssue] = useState(false);

  const [newTitle, setNewTitle] = useState("");

  const [newDesc, setNewDesc] = useState("");

  const [newImage, setNewImage] = useState(null);

  const [replyText, setReplyText] = useState("");

  const [statusFilter, setStatusFilter] = useState("all");

  const [submitting, setSubmitting] = useState(false);

  const [sendingReply, setSendingReply] = useState(false);

  const [updatingStatus, setUpdatingStatus] = useState(false);

  // ============================================================
  // ROLE
  // ============================================================

  const isLead = currentRole === "lead";

  const isAdmin = currentRole === "admin";

  const isParticipant = currentRole === "participant";

  // ============================================================
  // ACTIVE THREAD
  // ============================================================

  const activeThread =
    threads.find((t) => t.id === activeId) || null;

  // ============================================================
  // FILTER THREADS
  // ============================================================

  const visibleThreads = threads.filter((thread) => {
    if (statusFilter === "all") {
      return true;
    }

    return thread.status === statusFilter;
  });

  // ============================================================
  // STATUS COUNTS
  // ============================================================

  const openCount = threads.filter(
    (thread) => thread.status === "open"
  ).length;

  const inProgressCount = threads.filter(
    (thread) => thread.status === "in_progress"
  ).length;

  const resolvedCount = threads.filter(
    (thread) => thread.status === "resolved"
  ).length;

  // ============================================================
  // FETCH THREADS
  // ============================================================

  useEffect(() => {
    fetchThreads();
  }, []);

  async function fetchThreads() {
    setLoading(true);

    setErrorMsg("");

    try {
      const res = await api.get(
        webserviceUrl.get_community_threads
      );

      const rawPosts = res.data?.data || [];

      const mappedThreads = rawPosts.map(mapPostToThread);

      setThreads(mappedThreads);
    } catch (err) {
      console.error("Failed to fetch threads:", err);

      setErrorMsg(
        "Failed to load threads. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  // ============================================================
  // UPDATE STATUS
  // ============================================================

  async function resolveStatus(payload) {
    if (updatingStatus) return;

    setUpdatingStatus(true);

    setErrorMsg("");

    try {
      const res = await api.post(
        webserviceUrl.update_support_status,
        payload
      );

      if (res.data?.success) {
        // Refresh from backend so UI always represents DB state
        await fetchThreads();

        // If the active thread was resolved, keep detail page
        // open and let refreshed data update its status.
      } else {
        setErrorMsg(
          res.data?.message ||
            "Failed to update thread status."
        );
      }
    } catch (err) {
      console.error(
        "Failed to update thread status:",
        err
      );

      setErrorMsg(
        err.response?.data?.message ||
          "Failed to update thread status. Please try again."
      );
    } finally {
      setUpdatingStatus(false);
    }
  }

  // ============================================================
  // MARK THREAD RESOLVED
  // ============================================================

  function resolveThread() {
    if (!activeThread || !isLead || updatingStatus) {
      return;
    }

    resolveStatus({
      post_id: activeThread.id,
      status: "resolved",
    });
  }

  // ============================================================
  // CREATE NEW THREAD
  // ============================================================

  async function raiseIssue(e) {
    e.preventDefault();

    if (!newTitle.trim() || !newDesc.trim()) {
      return;
    }

    setSubmitting(true);

    setErrorMsg("");

    try {
      const formData = new FormData();

      formData.append(
        "post_description",
        newDesc.trim()
      );

      if (newImage) {
        formData.append("post_img", newImage);
      }

      const res = await api.post(
        webserviceUrl.post_community_thread,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const createdRaw =
        res.data?.data ||
        res.data?.post ||
        res.data;

      if (!createdRaw) {
        throw new Error(
          "Created post was not returned by API."
        );
      }

      const created = mapPostToThread(createdRaw);

      setThreads((prev) => [created, ...prev]);

      setNewTitle("");

      setNewDesc("");

      setNewImage(null);

      setShowNewIssue(false);

      setActiveId(created.id);
    } catch (err) {
      console.error("Failed to raise issue:", err);

      setErrorMsg(
        err.response?.data?.message ||
          "Failed to submit issue. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  // ============================================================
  // DELETE POST
  // ============================================================

  async function deletePost(postId) {
    if (!postId) return;

    setErrorMsg("");

    try {
      await api.post(
        webserviceUrl.delete_post,
        {
          post_id: postId,
        }
      );

      setThreads((prev) =>
        prev.filter((thread) => thread.id !== postId)
      );

      if (activeId === postId) {
        setActiveId(null);
      }
    } catch (err) {
      console.error("Failed to delete post:", err);

      setErrorMsg(
        err.response?.data?.message ||
          "Failed to delete post. Please try again."
      );
    }
  }

  // ============================================================
  // SEND REPLY
  // ============================================================

  async function sendReply(e) {
    e.preventDefault();

    if (
      !replyText.trim() ||
      !activeThread ||
      sendingReply
    ) {
      return;
    }

    // Don't allow reply on resolved thread
    if (activeThread.status === "resolved") {
      return;
    }

    const commentText = replyText.trim();

    const commentedSenderName = isLead
      ? "LEAD"
      : "You";

    const commentedDate = toSqlDateTime(new Date());

    setSendingReply(true);

    setErrorMsg("");

    try {
      const payload = {
        post_id: activeThread.id,

        comment: commentText,

        commented_sender_name:
          commentedSenderName,

        commented_date: commentedDate,
      };

      await api.post(
        webserviceUrl.post_comment,
        payload
      );

      const msg = {
        author: commentedSenderName,

        role: currentRole,

        text: commentText,

        at: commentedDate,
      };

      setThreads((prev) =>
        prev.map((thread) =>
          thread.id === activeThread.id
            ? {
                ...thread,

                messages: [
                  ...thread.messages,
                  msg,
                ],

                commentCount:
                  (thread.commentCount || 0) + 1,
              }
            : thread
        )
      );

      setReplyText("");
    } catch (err) {
      console.error(
        "Failed to post comment:",
        err
      );

      setErrorMsg(
        err.response?.data?.message ||
          "Failed to send reply. Please try again."
      );
    } finally {
      setSendingReply(false);
    }
  }

  // ============================================================
  // FORMAT DATE
  // ============================================================

  function formatDate(dateValue) {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleString(undefined, {
      day: "2-digit",

      month: "short",

      hour: "2-digit",

      minute: "2-digit",
    });
  }

  // ============================================================
  // RENDER ATTACHMENT
  // ============================================================

  function renderAttachment(rawUrl) {
    if (!rawUrl) {
      return null;
    }

    const fullUrl = getFullUrl(rawUrl);

    if (isImageUrl(rawUrl)) {
      return (
        <img
          className="sup-thread-image"
          src={fullUrl}
          alt=""
        />
      );
    }

    return (
      <a
        className="sup-file-chip"
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="sup-file-chip-icon">
          📄
        </span>

        <span className="sup-file-chip-name">
          {getFileName(rawUrl)}
        </span>
      </a>
    );
  }

  // ============================================================
  // LOADING
  // ============================================================

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p className="sup-empty">
            Loading threads...
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="page-wrapper">
      <div className="content mb-3">

        {/* =====================================================
            ERROR
        ====================================================== */}

        {errorMsg && (
          <p className="sup-empty">
            {errorMsg}
          </p>
        )}

        {/* =====================================================
            THREAD DETAIL
        ====================================================== */}

        {activeThread ? (
          <div className="sup-detail">

            {/* BACK */}

            <button
              type="button"
              className="sup-back-btn"
              onClick={() => setActiveId(null)}
            >
              ← Back to Support
            </button>

            {/* DETAIL HEADER */}

            <div className="sup-detail-head">

              <div>
                <h2>
                  {activeThread.title}
                </h2>

                <div className="sup-thread-meta">
                  <span>
                    {activeThread.raisedBy}
                  </span>

                  <span>·</span>

                  <span>
                    {formatDate(
                      activeThread.createdAt
                    )}
                  </span>
                </div>
              </div>

              {/* STATUS */}

              <span
                className={`sup-status-badge ${getStatusClass(
                  activeThread.status
                )}`}
              >
                {getStatusLabel(
                  activeThread.status
                )}
              </span>
            </div>

            {/* ATTACHMENT */}

            {renderAttachment(
              activeThread.image
            )}

            {/* SOCIAL */}

            <div className="sup-thread-social-row">

              <span
                className={`sup-like-pill${
                  activeThread.selfLiked
                    ? " sup-like-pill-active"
                    : ""
                }`}
              >
                ❤ {activeThread.likes}
              </span>

              <span className="sup-comment-pill">
                💬{" "}
                {activeThread.commentCount || 0}
              </span>
            </div>

            {/* ADMIN NOTE */}

            {isAdmin && (
              <div className="sup-admin-note">
                You're viewing as ADMIN —
                read-only. Only LEAD can reply or
                resolve.
              </div>
            )}

            {/* DELETE */}

            {isLead && activeThread.status !== "resolved" && (
              <button
                type="button"
                className="sup-secondary-btn"
                onClick={() =>
                  deletePost(activeThread.id)
                }
              >
                Delete this thread
              </button>
            )}

            {/* =================================================
                MESSAGES
            ================================================== */}

            <div className="sup-messages ">

              {activeThread.messages?.map(
                (message, index) => (
                  <div
                    key={
                      message.id || index
                    }
                    className={`sup-message stagger-children${
                      message.role === "lead"
                        ? " sup-message-lead"
                        : ""
                    }`}
                  >
                    <div className="sup-message-head">

                      <span className="sup-message-author">
                        {message.author}
                      </span>

                      <span className="sup-message-time">
                        {formatDate(
                          message.at
                        )}
                      </span>

                    </div>

                    <p>
                      {message.text}
                    </p>
                  </div>
                )
              )}

            </div>

            {/* =================================================
                REPLY FORM
            ================================================== */}

            {!isAdmin &&
              activeThread.status !==
                "resolved" && (
                <form
                  className="sup-reply-form"
                  onSubmit={sendReply}
                >
                  <textarea
                    rows={3}
                    placeholder={
                      isLead
                        ? "Reply as LEAD..."
                        : "Add a reply..."
                    }
                    value={replyText}
                    onChange={(e) =>
                      setReplyText(
                        e.target.value
                      )
                    }
                    disabled={
                      sendingReply ||
                      updatingStatus
                    }
                  />

                  <div className="sup-reply-actions">

                    {/* MARK RESOLVED */}

                    {isLead && (
                      <button
                        type="button"
                        className="sup-resolve-btn"
                        onClick={
                          resolveThread
                        }
                        disabled={
                          sendingReply ||
                          updatingStatus
                        }
                      >
                        {updatingStatus
                          ? "Resolving..."
                          : "✓ Mark resolved"}
                      </button>
                    )}

                    {/* SEND REPLY */}

                    <button
                      type="submit"
                      className="sup-primary-btn"
                      disabled={
                        sendingReply ||
                        updatingStatus ||
                        !replyText.trim()
                      }
                    >
                      {sendingReply
                        ? "Sending..."
                        : "Send reply"}
                    </button>

                  </div>
                </form>
              )}

            {/* =================================================
                RESOLVED MESSAGE
            ================================================== */}

            {activeThread.status ===
              "resolved" &&
              !isAdmin && (
                <p className="sup-resolved-note">
                  ✓ This thread has been marked
                  resolved.
                </p>
              )}

          </div>
        ) : (
          <>
            {/* =================================================
                HEADER
            ================================================== */}

            <div className="sup-header">

              <div>
                <h1 className="sup-title">
                  Support
                </h1>
              </div>

              {/* OPTIONAL:
              <button
                type="button"
                className="sup-primary-btn"
                onClick={() =>
                  setShowNewIssue(true)
                }
              >
                + Raise an issue
              </button>
              */}

            </div>

            {/* =================================================
                STATS
            ================================================== */}

            <div className="sup-stats-row">

              {/* TOTAL */}

              <div className="sup-stat">
                <span className="sup-stat-num">
                  {threads.length}
                </span>

                <span className="sup-stat-label">
                  Total threads
                </span>
              </div>

              {/* OPEN */}

              <div className="sup-stat">
                <span className="sup-stat-num sup-stat-num-open">
                  {openCount}
                </span>

                <span className="sup-stat-label">
                  Open
                </span>
              </div>

              {/* IN PROGRESS */}

              {/* <div className="sup-stat">
                <span className="sup-stat-num">
                  {inProgressCount}
                </span>

                <span className="sup-stat-label">
                  In Progress
                </span>
              </div> */}

              {/* RESOLVED */}

              <div className="sup-stat">
                <span className="sup-stat-num sup-stat-num-resolved">
                  {resolvedCount}
                </span>

                <span className="sup-stat-label">
                  Resolved
                </span>
              </div>

            </div>

            {/* =================================================
                FILTER TABS
            ================================================== */}

            <div className="sup-filter-row">

              {[
                "all",
                "open",
                // "in_progress",
                "resolved",
              ].map((filter) => (
                <button
                  key={filter}
                  type="button"
                  className={`sup-filter-tab${
                    statusFilter === filter
                      ? " sup-filter-tab-active"
                      : ""
                  }`}
                  onClick={() =>
                    setStatusFilter(filter)
                  }
                >
                  {filter === "all"
                    ? "All"
                    : filter === "open"
                    ? "Open"
                    // : filter === "in_progress"
                    // ? "In Progress"
                    : "Resolved"}
                </button>
              ))}

            </div>

            {/* =================================================
                THREAD LIST
            ================================================== */}

            <div className="sup-thread-list">

              {visibleThreads.length === 0 && (
                <p className="sup-empty">
                  No threads here yet.
                </p>
              )}

              {visibleThreads.map(
                (thread) => (
                  <div
                    key={thread.id}
                    className="sup-thread-card-wrap"
                  >

                    {/* THREAD CARD */}

                    <div
                      className="sup-thread-card"
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        setActiveId(
                          thread.id
                        )
                      }
                      onKeyDown={(e) => {
                        if (
                          e.key ===
                            "Enter" ||
                          e.key === " "
                        ) {
                          setActiveId(
                            thread.id
                          );
                        }
                      }}
                    >

                      <div className="sup-thread-card-top">

                        <h3>
                          {thread.title}
                        </h3>

                        {/* STATUS */}

                        <span
                          className={`sup-status-badge ${getStatusClass(
                            thread.status
                          )}`}
                        >
                          {getStatusLabel(
                            thread.status
                          )}
                        </span>

                      </div>

                      <p className="sup-thread-desc">
                        {thread.description}
                      </p>

                      {/* ATTACHMENT */}

                      {renderAttachment(
                        thread.image
                      )}

                      {/* RESOLVE */}

                      {thread.status !==
                        "resolved" && (
                        <button
                          type="button"
                          className="sup-card-delete-btn2"
                          style={{
                            display:
                              "block",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();

                            resolveStatus({
                              post_id:
                                thread.id,
                              status:
                                "resolved",
                            });
                          }}
                          disabled={
                            updatingStatus
                          }
                        >
                          {updatingStatus
                            ? "Resolving..."
                            : "Resolve"}
                        </button>
                      )}

                      {/* META */}

                      <div className="sup-thread-meta">

                        <span>
                          {thread.raisedBy}
                        </span>

                        <span>·</span>

                        <span>
                          {formatDate(
                            thread.createdAt
                          )}
                        </span>

                        <span>·</span>

                        <span>
                          {thread.messages
                            ?.length || 0}{" "}
                          message
                          {thread.messages
                            ?.length !==
                          1
                            ? "s"
                            : ""}
                        </span>

                      </div>

                      {/* SOCIAL */}

                      <div className="sup-thread-social-row">

                        <span className="sup-comment-pill">
                          💬{" "}
                          {thread.commentCount ||
                            0}
                        </span>

                      </div>

                    </div>

                    {/* DELETE */}

                    {isLead && thread.status !== "resolved" && (
                      <button
                        type="button"
                        className="sup-card-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();

                          deletePost(
                            thread.id
                          );
                        }}
                      >
                        Delete
                      </button>
                    )}

                  </div>
                )
              )}

            </div>
          </>
        )}

        {/* =====================================================
            NEW ISSUE MODAL
        ====================================================== */}

        {showNewIssue && (
          <div
            className="sup-modal-backdrop"
            onClick={() =>
              setShowNewIssue(false)
            }
          >
            <form
              className="sup-modal"
              onClick={(e) =>
                e.stopPropagation()
              }
              onSubmit={raiseIssue}
            >

              <h3>
                Raise an issue
              </h3>

              {/* TITLE */}

              <label className="sup-field-label">
                Title
              </label>

              <input
                type="text"
                value={newTitle}
                onChange={(e) =>
                  setNewTitle(
                    e.target.value
                  )
                }
                placeholder="Short summary of the issue"
                autoFocus
              />

              {/* DESCRIPTION */}

              <label className="sup-field-label">
                Description
              </label>

              <textarea
                rows={4}
                value={newDesc}
                onChange={(e) =>
                  setNewDesc(
                    e.target.value
                  )
                }
                placeholder="What's going wrong? Include steps to reproduce if you can."
              />

              {/* FILE */}

              <label className="sup-field-label">
                Attach image or file
                (optional)
              </label>

              <div className="sup-file-input-row">

                <label className="sup-choose-file-btn">

                  Choose file

                  <input
                    type="file"
                    className="sup-file-input-hidden"
                    onChange={(e) =>
                      setNewImage(
                        e.target.files?.[0] ||
                          null
                      )
                    }
                  />

                </label>

                <span
                  className={`sup-file-name${
                    newImage
                      ? ""
                      : " sup-file-name-empty"
                  }`}
                >
                  {newImage
                    ? newImage.name
                    : "No file chosen"}
                </span>

                {newImage && (
                  <button
                    type="button"
                    className="sup-file-clear-btn"
                    onClick={() =>
                      setNewImage(null)
                    }
                    aria-label="Remove file"
                  >
                    ✕
                  </button>
                )}

              </div>

              {/* ACTIONS */}

              <div className="sup-modal-actions">

                <button
                  type="button"
                  className="sup-secondary-btn"
                  onClick={() =>
                    setShowNewIssue(false)
                  }
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="sup-primary-btn"
                  disabled={submitting}
                >
                  {submitting
                    ? "Submitting..."
                    : "Submit to LEAD"}
                </button>

              </div>

            </form>
          </div>
        )}

      </div>
    </div>
  );
}